import React, { useEffect, useState } from 'react';

import { MINIMUM_PAGE_COUNT } from 'constants/page';

import useTitle from 'hooks/useTitle';
import { useDesignationAreasQuery, useDesignationsQuery } from 'hooks/query/useLookupQueries';
import { useExportPeopleFinderMutation, useFetchPeopleFinderQuery } from 'hooks/query/useUserQueries';

import Pagination from 'components/common/paginator';
import Loading from 'components/common/loading/Loading';

import { useQuery } from 'utils/queryParam';
import { handleError } from 'utils/errorHandler';

import { useAllSkillsQuery } from 'hooks/query/useSkillQueries';

import type { RouterProps } from 'interface/queryParam';

import { ResourceFilter, ResourceResults } from './components';

import type { DesignationRecord, PeopleFinderEmployeeRow, ResourceFinderFilterQuery } from './types';

const PAGE_COUNT = 10;

type PeopleFinderResponse = {
  data: PeopleFinderEmployeeRow[];
  meta: { total: number };
};

const ResourceFinder: React.FC<RouterProps> = (props) => {
  const [query, setQuery] = useQuery(props);

  const [exporting, setExporting] = useState(false);

  const pageSize = query.size ? +String(query.size) : PAGE_COUNT;

  const setTitle = useTitle();

  useEffect(() => {
    setTitle('People Finder');
  }, [setTitle]);

  const { page, ...filterQuery } = query;

  const { data: skills = [], isLoading: skillsLoading } = useAllSkillsQuery();

  useEffect(() => {
    if (!page) {
      setQuery({ ...query, page: 1 });
    }
  }, [page, query, setQuery]);

  const { data: designations = [], isLoading: loadingDesignations } = useDesignationsQuery();
  const { data: areas = [], isLoading: loadingAreas } = useDesignationAreasQuery();

  const {
    data: peopleFinderResponse,
    isLoading: loadingEmployees,
    isError: isPeopleFinderError,
  } = useFetchPeopleFinderQuery<PeopleFinderResponse>(query, Boolean(page));

  const { mutateAsync: exportPeopleFinder } = useExportPeopleFinderMutation();

  useEffect(() => {
    if (isPeopleFinderError) {
      handleError(new Error('Failed to load people finder results'));
    }
  }, [isPeopleFinderError]);

  const employees = peopleFinderResponse?.data ?? [];
  const total = peopleFinderResponse?.meta?.total ?? 0;
  const pageCount = Math.ceil(total / pageSize);

  const handlePageChange = (nextPage: number) => {
    setQuery({ ...query, page: nextPage });
  };

  const handleFilterClick = (newFilterState: ResourceFinderFilterQuery) => {
    setQuery({ ...query, ...newFilterState, page: 1 });
  };

  const handleExportClick = async () => {
    setExporting(true);

    try {
      const exportResponse = await exportPeopleFinder(query);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(exportResponse.data);
      link.download = 'leapfroggers skills.csv';
      link.click();

      setTimeout(() => URL.revokeObjectURL(link.href), 0);
    } finally {
      setExporting(false);
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setQuery({ ...query, size: nextPageSize, page: 1 });
  };

  const handleClearClick = () => {
    setQuery({ page: 1 });
  };

  return (
    <main>
      <div className="container">
        <div className="full-scope-card">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3 className="table-title">People Finder</h3>
            </div>
          </div>
          <div className="full-scope-card__content d-flex position-relative">
            <div className="resource-filter-area">
              <ResourceFilter
                skills={skills}
                initialState={filterQuery}
                designations={designations as DesignationRecord[]}
                areas={areas as DesignationRecord[]}
                onFilter={handleFilterClick}
                onClear={handleClearClick}
                loadingSkills={skillsLoading}
                loadingDesignations={loadingDesignations}
                loadingAreas={loadingAreas}
              />
            </div>
            <div className="resource-results-area">
              {loadingEmployees ? (
                <Loading />
              ) : (
                <>
                  <ResourceResults
                    employees={employees}
                    total={total}
                    exporting={exporting}
                    onExport={handleExportClick}
                  />
                  <Pagination
                    pageData={{ page: +String(page ?? '') }}
                    onPageChange={handlePageChange}
                    pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ResourceFinder;
